import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './OtpVerify.css';
import { errorToast, successToast } from '../../Utils/toast';
import { useDispatch } from 'react-redux';
import {
  verifyOtp,
  resendOtp,
  getStaffPermissions,
} from '../../redux/services/authService/authApi';
import { setLogin } from '../../redux/features/authSlice';
import { setToken } from '../../redux/apiAxios/cookies';
/* =========================================================
   CONFIG
========================================================= */
const OTP_LENGTH = 4;

const ORBIT_RADIUS = 110;
const BOX_SIZE = 62;
const LINE_BOX_W = 45;
const LINE_BOX_H = 45;
const LINE_GAP = 14;

const ROTATION_SPEED = 9;      
const SPIN_DURATION = 900;   

// const INITIAL_ANGLES = [0, 90, 180, 270];
const INITIAL_ANGLES = Array.from(
  { length: OTP_LENGTH },
  (_, i) => (360 / OTP_LENGTH) * i
);
export default function OtpVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();  

  const mobile = location.state?.mobile;
  const isNewUser = location.state?.isNewUser;
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [phase, setPhase] = useState('input'); // input | spinning | verifying | success
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [timer, setTimer] = useState(24);

  const boxRefs = useRef([]);
  const inputRefs = useRef([]);
  const orbitRef = useRef(null);
  const dotRef = useRef(null);
  const stageRef = useRef(null);

  const animationRef = useRef(null);
  const currentAngle = useRef(0);
  const spinStartTime = useRef(0);
  const redirectTimer = useRef(null);

  /* Redirect if no mobile */
  useEffect(() => {
    if (!mobile) navigate('/login', { replace: true });
  }, [mobile, navigate]);

  useEffect(() => {
    if (timer <= 0) return;
    const t = setTimeout(() => setTimer((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [timer]);

  /* Initial line layout */
  useEffect(() => {
    const t = setTimeout(() => {
      layoutLine();
      inputRefs.current[0]?.focus();
    }, 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, []);

  /* Cleanup */
  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (redirectTimer.current) clearTimeout(redirectTimer.current);
    };
  }, []);

  /* ---------- LAYOUTS ---------- */
  const layoutLine = () => {
    const total = LINE_BOX_W * OTP_LENGTH + LINE_GAP * (OTP_LENGTH - 1);
    const startX = (330 - total) / 2;

    boxRefs.current.forEach((box, i) => {
      if (!box) return;
      box.style.transition = 'left .35s ease, top .35s ease, width .35s ease, height .35s ease, border-radius .35s ease, transform .35s ease';
      box.style.left = `${startX + i * (LINE_BOX_W + LINE_GAP)}px`;
      box.style.top = '50%';
      box.style.width = `${LINE_BOX_W}px`;
      box.style.height = `${LINE_BOX_H}px`;
      box.style.borderRadius = '9px';
      box.style.transform = 'translateY(-50%)';
    });
  };

  const layoutOrbitStatic = () => {
    const cx = 165, cy = 165;
    boxRefs.current.forEach((box, i) => {
      if (!box) return;
      const angle = INITIAL_ANGLES[i];
      const rad = ((angle - 90) * Math.PI) / 180;
      const x = cx + ORBIT_RADIUS * Math.cos(rad);
      const y = cy + ORBIT_RADIUS * Math.sin(rad);

      box.style.transition = 'left .5s cubic-bezier(.34,.1,.25,1), top .5s cubic-bezier(.34,.1,.25,1), width .35s ease, height .35s ease, border-radius .35s ease, transform .5s cubic-bezier(.34,.1,.25,1)';
      box.style.left = `${x - BOX_SIZE / 2}px`;
      box.style.top = `${y - BOX_SIZE / 2}px`;
      box.style.width = `${BOX_SIZE}px`;
      box.style.height = `${BOX_SIZE}px`;
      box.style.borderRadius = '17px';
      box.style.transform = 'translate(0, 0)';
    });
  };

  /* ---------- ORBIT ANIMATION ---------- */
  const animateOrbit = (timestamp) => {
    if (!spinStartTime.current) spinStartTime.current = timestamp;
    const elapsed = timestamp - spinStartTime.current;
    const cx = 165, cy = 165;

    boxRefs.current.forEach((box, i) => {
      if (!box) return;
      const angle = INITIAL_ANGLES[i] + currentAngle.current;
      const rad = ((angle - 90) * Math.PI) / 180;
      const x = cx + ORBIT_RADIUS * Math.cos(rad);
      const y = cy + ORBIT_RADIUS * Math.sin(rad);

      box.style.transition = 'none';
      box.style.left = `${x - BOX_SIZE / 2}px`;
      box.style.top = `${y - BOX_SIZE / 2}px`;
      box.style.width = `${BOX_SIZE}px`;
      box.style.height = `${BOX_SIZE}px`;
      box.style.borderRadius = '17px';
      box.style.transform = 'translate(0, 0)';
    });

    currentAngle.current += ROTATION_SPEED;
    if (currentAngle.current >= 360) currentAngle.current -= 360;

    if (elapsed < SPIN_DURATION) {
      animationRef.current = requestAnimationFrame(animateOrbit);
    } else {
      stopOrbit();
      doVerify();
    }
  };

  const startOrbit = () => {
    setPhase('spinning');
    setErrorMsg('');
    setSuccessMsg('');

    orbitRef.current?.classList.add('show');
    dotRef.current?.classList.add('show');
    stageRef.current?.classList.remove('line-layout');

    layoutOrbitStatic();

    /* ⚡ jaldi start karo (800 → 400) */
    setTimeout(() => {
      currentAngle.current = 0;
      spinStartTime.current = 0;
      animationRef.current = requestAnimationFrame(animateOrbit);
    }, 400);
  };

  const stopOrbit = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  };

  // const doVerify = async () => {
  //   const otp = inputRefs.current.map((inp) => inp?.value || '').join('').trim();

  //   console.log('🔍 Verifying:', { mobile, otp });

  //   if (otp.length !== OTP_LENGTH) {
  //     setPhase('input');
  //     setErrorMsg('Please enter all 6 digits');
  //     resetToLine();
  //     return;
  //   }

  //   setPhase('verifying');

  //   try {
  //     const res = await verifyOtp(mobile, otp);

  //     console.log('✅ Success:', res);

  //     // ✅ SUCCESS
  //     setPhase('success');
  //     setSuccessMsg('OTP Verified Successfully');
  //     successToast(res.message || 'OTP verified successfully');

  //     // 1.5 sec baad auto redirect
  //     redirectTimer.current = setTimeout(() => {
  //       navigate('/', { replace: true });
  //     }, 1500);

  //   } catch (err) {
  //     console.error('❌ Error:', err);

  //     // ❌ ERROR — same page pe red message
  //     setPhase('input');
  //     setErrorMsg(err.message || 'Invalid OTP');
  //     errorToast(err.message || 'Invalid OTP');

  //     // Boxes shake
  //     boxRefs.current.forEach((box) => {
  //       box?.animate(
  //         [
  //           { transform: 'translateY(-50%) translateX(-6px)' },
  //           { transform: 'translateY(-50%) translateX(6px)' },
  //           { transform: 'translateY(-50%) translateX(-4px)' },
  //           { transform: 'translateY(-50%) translateX(4px)' },
  //           { transform: 'translateY(-50%) translateX(0)' },
  //         ],
  //         { duration: 400 }
  //       );
  //     });

  //     // 1.5 sec baad reset
  //     setTimeout(() => resetToLine(), 1500);
  //   }
  // };

  const doVerify = async () => {
    const otp = inputRefs.current.map((inp) => inp?.value || '').join('').trim();
    if (otp.length !== OTP_LENGTH) {
      setPhase('input');
      setErrorMsg('Please enter all 4 digits');
      resetToLine();
      return;
    }
    setPhase('verifying');
    try {
      const res = await verifyOtp(mobile, otp);
      const message = res?.message;
      const token = res?.data?.token;
      const vendor = res?.data?.vendor;
      const loginType = res?.login_type;
      if (!token || !vendor) {
        throw new Error('Token or admin missing in verify response');
      }
      setToken(token);

      localStorage.setItem('admin', JSON.stringify(vendor));
      localStorage.setItem('role', vendor?.role);
      localStorage.setItem('status', vendor?.status);
      localStorage.setItem('kyc_status', vendor?.kyc_status);
      localStorage.setItem('login_type', loginType || ""); 
      // const permissionRes = await getStaffPermissions(vendor.id); 
      dispatch(
        setLogin({
          admin: vendor,
          token,
          role: vendor.role,
          // permissions: permissionRes.data.staff_permission,
          permissions: {},
          category_id: vendor.category_id,
          subcategory_id: vendor.subcategory_id,
          module: vendor.module_id
        })
      );
      setPhase('success');
      setSuccessMsg('OTP Verified Successfully');
      successToast(message || 'OTP verified successfully');
     redirectTimer.current = setTimeout(() => {
      navigate('/', { replace: true });
    }, 1500);

    } catch (err) {
      console.error('❌ Error:', err);

      const errMsg =
        err?.response?.data?.message ||
        err?.message ||
        'Invalid OTP';

      setPhase('input');
      setErrorMsg(errMsg);
      errorToast(errMsg);

      // shake animation
      boxRefs.current.forEach((box) => {
        box?.animate(
          [
            { transform: 'translateY(-50%) translateX(-6px)' },
            { transform: 'translateY(-50%) translateX(6px)' },
            { transform: 'translateY(-50%) translateX(-4px)' },
            { transform: 'translateY(-50%) translateX(4px)' },
            { transform: 'translateY(-50%) translateX(0)' },
          ],
          { duration: 400 }
        );
      });

      setTimeout(() => resetToLine(), 1500);
    }
  };
  /* ---------- RESET ---------- */
  const resetToLine = () => {
    stopOrbit();
    setPhase('input');
    currentAngle.current = 0;
    spinStartTime.current = 0;
    orbitRef.current?.classList.remove('show');
    dotRef.current?.classList.remove('show');
    stageRef.current?.classList.add('line-layout');

    inputRefs.current.forEach((inp) => {
      if (inp) inp.value = '';
    });
    setDigits(Array(OTP_LENGTH).fill(''));
    setErrorMsg('');
    setSuccessMsg('');

    layoutLine();
    setTimeout(() => inputRefs.current[0]?.focus(), 150);
  };

  const handleChange = (index, value) => {
    if (errorMsg) setErrorMsg('');

    const v = value.replace(/\D/g, '').slice(0, 1);
    const next = [...digits];
    next[index] = v;
    setDigits(next);

    if (v && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    const entered = next.join('');
    if (entered.length === OTP_LENGTH && phase === 'input') {
      setTimeout(() => startOrbit(), 200);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) inputRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) inputRefs.current[index + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    setErrorMsg('');
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((n, i) => { next[i] = n; });
    setDigits(next);

    inputRefs.current.forEach((inp, i) => {
      if (inp) inp.value = next[i] || '';
    });

    if (pasted.length === OTP_LENGTH && phase === 'input') {
      inputRefs.current[OTP_LENGTH - 1]?.focus();
      setTimeout(() => startOrbit(), 200);
    }
  };

  const handleResend = async () => {
    if (timer > 0 || phase !== 'input') return;
    try {
      const res = await resendOtp(mobile);
      successToast(res.message || 'OTP resent');
      setTimer(24);
      resetToLine();
    } catch (err) {
      errorToast(err.message || 'Failed to resend OTP');
    }
  };
  const isSuccess = phase === 'success';

  return (
    <div className="otp-page">
      <div className="otp-card">
        <div className="otp-card-handle"></div>
        <div className={`otp-verify-screen ${isSuccess ? 'hide' : ''}`}>
          <h2>Verify your number</h2>
          <p className="otp-description">
            {isNewUser ? 'Register with ' : 'Login with '}
            <strong>+91 {mobile}</strong>
          </p>

          <div className="otp-stage line-layout" ref={stageRef}>
            <svg className="otp-orbit-svg" ref={orbitRef} viewBox="0 0 100 100">
              <circle className="otp-orbit-ring" cx="50" cy="50" r="50" />
            </svg>

            <div className="otp-center-dot" ref={dotRef}></div>

            {Array.from({ length: OTP_LENGTH }).map((_, i) => (
              <label
                key={i}
                className="otp-box"
                ref={(el) => (boxRefs.current[i] = el)}
              >
                <input
                  ref={(el) => (inputRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digits[i]}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  onPaste={i === 0 ? handlePaste : undefined}
                  disabled={phase !== 'input'}
                  autoComplete="one-time-code"
                />
              </label>
            ))}
          </div>

          {/* Status message */}
          <div className={`otp-status ${errorMsg ? 'error' : successMsg ? 'success' : ''}`}>
            {errorMsg || successMsg}
          </div>

          <div className="otp-resend">
            Didn't receive the code?
            <button onClick={handleResend} disabled={timer > 0 || phase !== 'input'}>
              {timer > 0 ? `Resend in ${timer}s` : 'Resend OTP'}
            </button>
          </div>
        </div>

        {/* ============ SUCCESS SCREEN ============ */}
        <div className={`otp-success-screen ${isSuccess ? 'show' : ''}`}>
          <div className="otp-success-title">OTP Verified Successfully</div>
          <div className="otp-success-text">Redirecting to dashboard...</div>
          <div className="otp-success-animation">
            <div className="otp-success-circle"></div>
            <div className="otp-check-box"><span className="otp-check"></span></div>
          </div>
        </div>
      </div>
    </div>
  );
}